import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Check, Star, Zap, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'wouter';
import config, { COMPANY } from '@/config';
import { apiFetch, buildAuthHeaders } from '@/utils/api';
import { calculateDaysRemaining, isSubscriptionCancelled, formatExpiryDate } from '@/utils/subscriptionHelpers';
import SubscriptionSuccessModal, { ActionType } from '@/components/SubscriptionSuccessModal';

interface PlanFeature {
  text: string;
  included: boolean;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  period: string;
  popular?: boolean;
  icon: React.ReactNode;
  features: PlanFeature[];
  buttonText: string;
  buttonVariant: 'default' | 'secondary' | 'destructive';
}

const plans: SubscriptionPlan[] = [
  {
    id: 'free',
    name: 'Free',
    description: 'Perfect for trying out the platform',
    price: 0,
    period: 'month',
    icon: <Zap className="w-6 h-6"/>,
    features: [
      { text: 'Unlimited matching', included: true },
      { text: 'HD video quality', included: true },
      { text: 'Ad-free experience', included: true },
      { text: 'Advanced filters', included: true },
      { text: 'Priority matching', included: false },
      { text: 'Gender-based matching', included: false },
      { text: 'Country-based matching', included: false }
    ],
    buttonText: 'Free Plan',
    buttonVariant: 'secondary'
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Enhanced features for regular users',
    price: 2.99,
    period: 'month',
    popular: true,
    icon: <Star className="w-6 h-6"/>,
    features: [
      { text: 'Unlimited matching', included: true },
      { text: 'HD video quality', included: true },
      { text: 'Ad-free experience', included: true },
      { text: 'Advanced filters', included: true },
      { text: 'Priority matching', included: true },
      { text: 'Gender-based matching', included: true },
      { text: 'Country-based matching', included: true }
    ],
    buttonText: 'Upgrade to Pro',
    buttonVariant: 'default'
  }
];

const urlParams = new URLSearchParams(window.location.search);

export default function SubscriptionPlans() {
  const [ isLoading, setIsLoading ] = useState<string | null>(null);
  const [ error, setError ] = useState<string | null>(null);
  const [ hoveredPlan, setHoveredPlan ] = useState<string | null>(null);
  const [ showCancelDialog, setShowCancelDialog ] = useState(false);
  const [ planToCancel, setPlanToCancel ] = useState<string | null>(null);
  const [ showSuccessModal, setShowSuccessModal ] = useState(false);
  const [ successAction, setSuccessAction ] = useState<ActionType>('subscribe');
  const { user, getIdToken, getCsrfToken } = useAuth();
  const [ , navigate ] = useLocation();

  // Check if returned back from payment callback
  const returnedFromSubscription = urlParams.get('customer_session_token');

  // Check for success action from URL params (e.g., after payment callback)
  useEffect(() => {
    if (returnedFromSubscription) {
      setSuccessAction('subscribe');
      setShowSuccessModal(true);

      // Clean up URL
      urlParams.delete('customer_session_token');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, [ returnedFromSubscription ]);

  // Check subscription cancellation status
  const isCancelled = isSubscriptionCancelled(user?.maybeSubscriptionInfo?.status);
  const daysRemaining = isCancelled && user?.maybeSubscriptionInfo?.maybeExpiryDate
    ? calculateDaysRemaining(user.maybeSubscriptionInfo.maybeExpiryDate)
    : 0;
  const expiryDate = user?.maybeSubscriptionInfo?.maybeExpiryDate
    ? formatExpiryDate(user.maybeSubscriptionInfo.maybeExpiryDate)
    : '';

  // Normalize user plan for comparison (case-insensitive)
  const userPlan = user?.maybeSubscriptionInfo?.plan?.toLowerCase() || 'free';

  const handlePlanSelect = async (planId: string) => {
    if (planId === 'free') return;

    setIsLoading(planId);
    try {
      const token = getIdToken();
      if (!token) throw new Error('Authentication required');

      const response = await apiFetch(`${config.baseUrl}/${config.endpoints.createCheckoutSession}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          productIds: [ config.paymentProducts.proSubscription ],
          purpose: 'subscription'
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payload && data.payload.url) {
          window.location.href = data.payload.url;
        }
      } else {
        setError('Unable to process payment');
      }
    } catch (error) {
      console.error('Failed to subscribe to plan:', error);
    } finally {
      setIsLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!planToCancel) return;

    setIsLoading(planToCancel);
    try {
      const token = getIdToken();
      if (!token) throw new Error('Authentication required');

      const csrfToken = getCsrfToken();
      const response = await fetch(`${config.baseUrl}/${config.endpoints.cancelSubscription}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: buildAuthHeaders(token, csrfToken)
      });

      if (!response.ok) {
        setError('Unable to cancel subscription');
      } else {
        console.log('Subscription cancelled successfully');
        setShowCancelDialog(false);
        setSuccessAction('cancel');
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    } finally {
      setIsLoading(null);
    }
  };

  const handleCancelButtonClick = (planId: string) => {
    setPlanToCancel(planId);
    setShowCancelDialog(true);
  };

  const handleReactivateSubscription = async (planId: string) => {
    setIsLoading(planId);
    try {
      const token = getIdToken();
      if (!token) throw new Error('Authentication required');

      const csrfToken = getCsrfToken();
      const response = await fetch(`${config.baseUrl}/${config.endpoints.uncancelSubscription}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: buildAuthHeaders(token, csrfToken)
      });

      if (!response.ok) {
        setError('Unable to reactivate subscription');
      } else {
        console.log('Subscription reactivated successfully');
        setSuccessAction('reactivate');
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
      setError('Unable to reactivate subscription');
    } finally {
      setIsLoading(null);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0E1525] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-2">Something went wrong</h1>
          <p className="text-gray-400">Please try again later</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0E1525] py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => {
              navigate('/');
            }}
            className="flex items-center gap-2 text-gray-400 hover:text-white hover:bg-slate-700"
          >
            <ArrowLeft className="h-4 w-4"/>
            Back to Chat
          </Button>
        </div>
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Unlock premium features and enhance your video chat experience with our subscription plans
          </p>
        </div>
        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative p-8 bg-[#1A2332] border-gray-700 transition-all duration-300 hover:border-[#11B981] ${
                plan.popular ? 'ring-2 ring-[#11B981] border-[#11B981]' : ''
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <Badge
                  className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#11B981] text-white px-4 py-1">
                  Best Experience
                </Badge>
              )}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#11B981]/10 rounded-lg mb-4">
                  <div className="text-[#11B981]">{plan.icon}</div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                {/* Cancellation indicator for this plan */}
                {isCancelled && userPlan === plan.id && plan.id !== 'free' && (
                  <div className="mb-4 p-3 bg-orange-900/20 border border-orange-500/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-orange-400 flex-shrink-0"/>
                      <span className="text-sm font-semibold text-orange-300">Subscription Cancelled</span>
                    </div>
                    <p className="text-xs text-gray-300 mb-2">
                      You will continue to have access to Pro features until your subscription expires.
                    </p>
                    <div className="flex flex-col gap-1 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Days remaining:</span>
                        <span
                          className="font-semibold text-orange-300">{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Expires on:</span>
                        <span className="font-semibold text-orange-300">{expiryDate}</span>
                      </div>
                    </div>
                  </div>
                )}
                <p className="text-gray-400 text-sm mb-4">{plan.description}</p>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">
                    ${plan.price}
                  </span>
                  <span className="text-gray-400 ml-1">/{plan.period}</span>
                </div>
              </div>
              {/* Features */}
              <div className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <div className={`flex-shrink-0 mr-3 ${
                      feature.included ? 'text-[#11B981]' : 'text-gray-500'
                    }`}>
                      <Check className="w-4 h-4"/>
                    </div>
                    <span className={`text-sm ${
                      feature.included ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>
              {/* Action Button */}
              <Button
                onClick={() => {
                  if (isCancelled && userPlan === plan.id && plan.id !== 'free') {
                    handleReactivateSubscription(plan.id);
                  } else if (userPlan === plan.id && plan.id !== 'free') {
                    handleCancelButtonClick(plan.id);
                  } else {
                    handlePlanSelect(plan.id);
                  }
                }}
                onMouseEnter={() => setHoveredPlan(plan.id)}
                onMouseLeave={() => setHoveredPlan(null)}
                disabled={isLoading === plan.id || plan.id === 'free'}
                className={`w-full py-3 font-medium transition-colors duration-200 ${
                  isCancelled && userPlan === plan.id && plan.id !== 'free'
                    ? 'bg-[#11B981] hover:bg-[#0EA874] text-white'
                    : userPlan === plan.id && plan.id !== 'free' && hoveredPlan === plan.id
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : userPlan === plan.id
                        ? 'bg-gray-600 text-gray-400'
                        : plan.buttonVariant === 'default'
                          ? 'bg-[#11B981] hover:bg-[#0EA874] text-white'
                          : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
              >
                {isLoading === plan.id ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"/>
                    Processing...
                  </div>
                ) : isCancelled && userPlan === plan.id && plan.id !== 'free' ? (
                  'Reactivate Subscription'
                ) : userPlan === plan.id ? (
                  userPlan !== 'free' && hoveredPlan === plan.id ? 'Cancel Subscription' : 'Current Plan'
                ) : userPlan === 'free' && plan.id === 'free' ? (
                  'Current Plan'
                ) : (
                  plan.buttonText
                )}
              </Button>
            </Card>
          ))}
        </div>
        {/* Cancellation Confirmation Dialog */}
        <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <AlertDialogContent className="bg-[#1A2332] border-gray-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Cancel Subscription</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                Are you sure you want to cancel your subscription? You&#39;ll be downgraded to the Free plan and lose access
                to premium features. Your access will continue until the end of your current billing period.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => setShowCancelDialog(false)}
                className="bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-600"
              >
                Keep Subscription
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancelSubscription}
                disabled={isLoading !== null}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"/>
                    Cancelling...
                  </div>
                ) : (
                  'Cancel Subscription'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <Card className="p-6 bg-[#1A2332] border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-2">
                Can I cancel my subscription anytime?
              </h3>
              <p className="text-gray-400">
                Yes, you can cancel your subscription at any time. Your access will continue until the end of your
                current billing period.
              </p>
            </Card>

            <Card className="p-6 bg-[#1A2332] border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-2">
                What happens if I upgrade?
              </h3>
              <p className="text-gray-400">
                When you upgrade, your new plan takes effect immediately and you&#39;ll be charged the full monthly amount.
                Your billing cycle will run from the date you subscribed for one month.
              </p>
            </Card>

            <Card className="p-6 bg-[#1A2332] border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-2">
                Are there any additional fees?
              </h3>
              <p className="text-gray-400">
                No hidden fees! The price you see is what you pay. Taxes may apply based on your location.
              </p>
            </Card>
          </div>
        </div>
        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 mb-4">
            Need help choosing the right plan?
          </p>
          <a href={`mailto:${COMPANY.supportEmail}`}>
            <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
              Contact Support
            </Button>
          </a>
        </div>
      </div>
      {/* Success Modal */}
      <SubscriptionSuccessModal
        open={showSuccessModal}
        action={successAction}
        onCountdownComplete={() => window.location.href = '/'}
      />
    </div>
  );
}
